import Handlebars from 'handlebars';
import { P, match } from 'ts-pattern';
import { initConsoleLogger } from '@salsita-ai/platform-common/utils';
import { createRecipe, implementCustomExpressions, implementCustomInvariants, invariantBuilder } from '../__generated/implementation.js';
import { FixingSystemType, HandrailType, MetalPostStyleType, PostType } from '../__generated/model/index.js';
import { DeckDataBindings } from '../data-bindings/deck.js';
import { getProductVariant } from '../data-bindings/pricing.js';
import { ProductsMap, isHandrailSku, isWireSku } from '../data-bindings/products.js';
import { getSketchingLevelHeights } from '../projections/get-level-heights.js';
/**
 * The fallback price is used when the product variant is not found in the backend data. As
 * so many products are not yet available, this is temporarily set to 0. When all products
 * should be available, it needs to be changed to NaN and add to cart will be blocked.
 */ const FALLBACK_PRICE = 0;
const logger = initConsoleLogger();
export const deckInvariantExpressions = invariantBuilder.withInvariants(implementCustomInvariants({
    Deck: {
        noSeparateBranches: (s, state, ctx)=>{
            return !(s.$items.length > 1 && s.$items.some((item)=>{
                const shape = ctx.sketchingProjections.getEntityShape(item.id, state, 'main');
                return shape.connections.length === 0;
            }));
        },
        noClosedShapes: (s, state, ctx)=>{
            if (s.$items.length === 0) {
                return true;
            }
            const sketching = ctx.sketchingProjections.getSketching(state, 'main');
            const hasConnectedStairs = sketching.connections.some((connection)=>connection.from.shape.type === 'rectangle' && connection.to.shape.type === 'rectangle');
            if (hasConnectedStairs) {
                return true;
            }
            const result = s.$shapeHeights;
            return result.valid;
        },
        connectedStairs: (_, state, ctx)=>{
            const sketching = ctx.sketchingProjections.getSketching(state, 'main');
            return !sketching.connections.some((connection)=>{
                return connection.from.shape.type === 'rectangle' && connection.to.shape.type === 'rectangle';
            });
        },
        noIntermediateWithRoundPosts: (s)=>{
            return !(s.postType === PostType.Metal && s.postStyle === MetalPostStyleType.RoundStainless && s.useThinIntermediatePosts);
        },
        stainlessHandrail: (s)=>{
            return !((s.postStyle === MetalPostStyleType.Custom || s.postStyle === MetalPostStyleType.SquareAlu) && (s.handrailType === HandrailType.RoundStainless || s.handrailType === HandrailType.SquareStainless));
        },
        only45Degrees: (_, state, ctx)=>{
            const sketching = ctx.sketchingProjections.getSketching(state, 'main');
            const result = sketching.connections.every((connection)=>{
                const angleModFull = connection.angle % (2 * Math.PI);
                const isZeroOrTwoPi = Math.abs(angleModFull) < 1e-6 || Math.abs(angleModFull - 2 * Math.PI) < 1e-6;
                if (isZeroOrTwoPi) {
                    return true;
                }
                const angleMod = connection.angle % (Math.PI / 4);
                const closeTo45 = Math.abs(angleMod) < 1e-3 || Math.abs(angleMod - Math.PI / 4) < 1e-3;
                return closeTo45;
            });
            return result;
        },
        no45DegToggleLock: (s, state, ctx)=>{
            const sketching = ctx.sketchingProjections.getSketching(state, 'main');
            const allowedAngles = [
                0,
                Math.PI / 2,
                Math.PI,
                3 * Math.PI / 2,
                2 * Math.PI
            ];
            const result = !sketching.connections.some((connection)=>{
                if (allowedAngles.some((angle)=>Math.abs(connection.angle - angle) < 1e-6)) {
                    return false;
                }
                const angleMod = connection.angle % (Math.PI / 4);
                const closeTo45 = Math.abs(angleMod) < 1e-3 || Math.abs(angleMod - Math.PI / 4) < 1e-3;
                return closeTo45 && s.fixingSystem === FixingSystemType.ToggleLock && (s.postStyle === MetalPostStyleType.SquareStainless || s.postStyle === MetalPostStyleType.SquareAlu);
            });
            return result;
        }
    }
})).buildViolationExpressions();
export const deckExpressions = implementCustomExpressions({
    Deck: {
        $postSize: (s)=>{
            return match([
                s.postType,
                s.postStyle
            ]).with([
                PostType.Metal,
                MetalPostStyleType.SquareAlu
            ], ()=>50).with([
                PostType.Metal,
                MetalPostStyleType.RoundStainless
            ], ()=>50).with([
                PostType.Metal,
                MetalPostStyleType.SquareStainless
            ], ()=>50).with([
                PostType.Metal,
                MetalPostStyleType.Custom
            ], ()=>50).with([
                PostType.Timber,
                P.any
            ], ()=>100).exhaustive();
        },
        $numberOfWires: (s)=>{
            if (s.postType === PostType.Timber) {
                return s.numberOfWires;
            }
            if (s.postType === PostType.Metal && s.postStyle === MetalPostStyleType.Custom) {
                return s.numberOfWires;
            }
            return 11;
        },
        $boundingBox: (_, state, ctx)=>{
            const sketching = ctx.sketchingProjections.getSketching(state, 'main');
            const minDimensions = 0;
            const padding = 1500;
            const mostLeftPoint = sketching.shapes.reduce((acc, shape)=>{
                if (shape.type === 'line') {
                    const points = [
                        shape.start,
                        shape.end
                    ];
                    return points.reduce((acc, point)=>{
                        return acc < point.x ? acc : point.x;
                    }, acc);
                }
                const points = [
                    shape.topLeft,
                    shape.topRight,
                    shape.bottomLeft,
                    shape.bottomRight
                ];
                return points.reduce((acc, point)=>{
                    return acc < point.x ? acc : point.x;
                }, acc);
            }, -minDimensions);
            const mostRightPoint = sketching.shapes.reduce((acc, shape)=>{
                if (shape.type === 'line') {
                    const points = [
                        shape.start,
                        shape.end
                    ];
                    return points.reduce((acc, point)=>{
                        return acc > point.x ? acc : point.x;
                    }, acc);
                }
                const points = [
                    shape.topLeft,
                    shape.topRight,
                    shape.bottomLeft,
                    shape.bottomRight
                ];
                return points.reduce((acc, point)=>{
                    return acc > point.x ? acc : point.x;
                }, acc);
            }, minDimensions);
            const mostTopPoint = sketching.shapes.reduce((acc, shape)=>{
                if (shape.type === 'line') {
                    const points = [
                        shape.start,
                        shape.end
                    ];
                    return points.reduce((acc, point)=>{
                        return acc > point.y ? acc : point.y;
                    }, acc);
                }
                const points = [
                    shape.topLeft,
                    shape.topRight,
                    shape.bottomLeft,
                    shape.bottomRight
                ];
                return points.reduce((acc, point)=>{
                    return acc > point.y ? acc : point.y;
                }, acc);
            }, minDimensions);
            const mostBottomPoint = sketching.shapes.reduce((acc, shape)=>{
                if (shape.type === 'line') {
                    const points = [
                        shape.start,
                        shape.end
                    ];
                    return points.reduce((acc, point)=>{
                        return acc < point.y ? acc : point.y;
                    }, acc);
                }
                const points = [
                    shape.topLeft,
                    shape.topRight,
                    shape.bottomLeft,
                    shape.bottomRight
                ];
                return points.reduce((acc, point)=>{
                    return acc < point.y ? acc : point.y;
                }, acc);
            }, -minDimensions);
            const bbox = {
                min: {
                    x: mostLeftPoint - padding,
                    z: mostBottomPoint - padding,
                    y: 0
                },
                max: {
                    x: mostRightPoint + padding,
                    z: mostTopPoint + padding,
                    y: 0
                }
            };
            if (bbox.min.x === 0 && bbox.max.x === 0) {
                return {
                    min: {
                        x: -3000,
                        z: -3000,
                        y: 0
                    },
                    max: {
                        x: 3000,
                        z: 3000,
                        y: 0
                    }
                };
            }
            return bbox;
        },
        $shapeHeights: (_, state, ctx)=>{
            const edges = ctx.sketchingProjections.getSketchingTerminalPoints(state, 'main');
            const { shapes } = ctx.sketchingProjections.getSketching(state, 'main');
            const result = getSketchingLevelHeights(edges, shapes, (shape)=>{
                if (shape.type === 'line') {
                    return 0;
                }
                const connectedEntity = ctx.domain.get$Entity(shape.instance.entityId, state);
                return connectedEntity.$height;
            });
            return result;
        },
        $isStainless: DeckDataBindings.postStyle.bind('isStainless'),
        $finishSwatches: DeckDataBindings.finishSwatches.bind('value'),
        $thinIntermediatePostSwatches: DeckDataBindings.thinIntermediatePostSwatches.bind('value'),
        $postShape: DeckDataBindings.postShape.bind('postShape'),
        $allSkus: (s)=>{
            return s.$items.flatMap((item)=>item.$skus);
        },
        $handrailSkus: (s)=>{
            return s.$items.flatMap((item)=>item.$skus).filter((item)=>isHandrailSku(item.sku));
        },
        $wireSkus: (s)=>{
            return s.$items.flatMap((item)=>item.$skus).filter((item)=>isWireSku(item.sku));
        },
        $otherSkus: (s)=>{
            return s.$items.flatMap((item)=>item.$skus).filter((item)=>!isWireSku(item.sku) && !isHandrailSku(item.sku));
        },
        $wireProducts: (s, _state, ctx)=>{
            const wireProduct = s.$wireSkus.at(0);
            if (!wireProduct) {
                return [];
            }
            const totalWireLength = s.$wireSkus.reduce((acc, item)=>{
                return acc + item.metadata['length'];
            }, 0);
            const wireLengths = Array.from(new Set(s.$wireSkus.map((item)=>item.metadata['length'])));
            const wireProductCount = Math.floor(totalWireLength / 1000);
            const wireProductDef = ProductsMap[wireProduct.sku];
            if (!wireProductDef) {
                throw new Error(`Wire product not found: ${wireProduct.sku}`);
            }
            const wireProductVariant = getProductVariant(wireProduct.sku, ctx.backendData);
            if (!wireProductVariant) {
                // TODO: When all the products have been added to Shopify, this needs to be handled more strictly.
                logger.warn(`Wire product variant not found: ${wireProduct.sku}. No price available, can not add this product to cart.`);
            }
            const additionalDescription = 'Lengths: ' + wireLengths.map((length)=>`${length.toFixed(0)} mm`).join(', ');
            return Array.from({
                length: wireProductCount
            }, ()=>({
                    ...wireProductDef,
                    subtitle: (wireProductVariant?.displayName ?? `(NOT FOUND) ${wireProductDef.subtitle}`) + '. ' + additionalDescription,
                    key: 'wire',
                    price: wireProductVariant?.price ?? FALLBACK_PRICE,
                    shopifyProductVariantId: wireProductVariant?.id ?? undefined,
                    additionalDescription
                }));
        },
        $handrailProducts: (s, _state, ctx)=>{
            const handrailProduct = s.$handrailSkus.at(0);
            if (!handrailProduct) {
                return [];
            }
            const totalHandrailLength = s.$handrailSkus.reduce((acc, item)=>{
                return acc + item.metadata['length'];
            }, 0);
            const handrailLengths = Array.from(new Set(s.$handrailSkus.map((item)=>item.metadata['length'])));
            const handrailProductCount = Math.ceil(totalHandrailLength / 1000);
            const handrailProductDef = ProductsMap[handrailProduct.sku];
            if (!handrailProductDef) {
                throw new Error(`Handrail product not found: ${handrailProduct.sku}`);
            }
            const handrailProductVariant = getProductVariant(handrailProduct.sku, ctx.backendData);
            if (!handrailProductVariant) {
                // TODO: When all the products have been added to Shopify, this needs to be handled more strictly.
                logger.warn(`Handrail product variant not found: ${handrailProduct.sku}. No price available, can not add this product to cart.`);
            }
            const additionalDescription = 'Lengths: ' + handrailLengths.map((length)=>`${length.toFixed(0)} mm`).join(', ');
            return Array.from({
                length: handrailProductCount
            }, ()=>({
                    ...handrailProductDef,
                    subtitle: (handrailProductVariant?.displayName ?? `(NOT FOUND) ${handrailProductDef.subtitle}`) + '. ' + additionalDescription,
                    key: handrailProduct.sku,
                    price: handrailProductVariant?.price ?? FALLBACK_PRICE,
                    shopifyProductVariantId: handrailProductVariant?.id ?? undefined,
                    additionalDescription
                }));
        },
        $otherProducts: (s, _state, ctx)=>{
            return s.$otherSkus.map((sku)=>{
                const product = ProductsMap[sku.sku];
                if (!product) {
                    logger.error(`Product not found: ${sku.sku}`);
                    return {
                        title: `NOT FOUND: ${sku.sku}`,
                        subtitle: '',
                        key: sku.sku + JSON.stringify(sku.metadata),
                        price: FALLBACK_PRICE
                    };
                }
                const otherProductVariant = getProductVariant(sku.sku, ctx.backendData);
                if (!otherProductVariant) {
                    // TODO: When all the products have been added to Shopify, this needs to be handled more strictly.
                    logger.warn(`Other product variant not found: ${sku.sku}. No price available, can not add this product to cart.`);
                }
                const title = Handlebars.compile(product.title)(sku.metadata);
                const productSubtitle = otherProductVariant?.displayName ?? `(NOT FOUND) ${product.subtitle}`;
                const subtitle = Handlebars.compile(productSubtitle)(sku.metadata);
                return {
                    ...product,
                    title,
                    subtitle,
                    key: sku.sku + JSON.stringify(sku.metadata),
                    price: otherProductVariant?.price ?? FALLBACK_PRICE,
                    shopifyProductVariantId: otherProductVariant?.id ?? undefined
                };
            });
        },
        $linkProduct: (s, _state, ctx)=>{
            const linkProductDef = ProductsMap[s.$linkProductSku];
            if (!linkProductDef) {
                throw new Error(`Link product not found: ${s.$linkProductSku}`);
            }
            const linkProductVariant = getProductVariant(s.$linkProductSku, ctx.backendData);
            if (!linkProductVariant) {
                // TODO: When all the products have been added to Shopify, this needs to be handled more strictly.
                logger.warn(`Link product variant not found: ${s.$linkProductSku}. Can not add this product to cart.`);
            }
            return {
                ...linkProductDef,
                subtitle: linkProductVariant?.displayName ?? `(NOT FOUND) ${linkProductDef.subtitle}`,
                key: 'link',
                price: linkProductVariant?.price ?? FALLBACK_PRICE,
                shopifyProductVariantId: linkProductVariant?.id ?? undefined
            };
        },
        $products: (s)=>{
            return [
                ...s.$otherProducts,
                ...s.$handrailProducts,
                ...s.$wireProducts
            ];
        },
        $productsForCart: (s)=>{
            const productsForCart = s.$summary.reduce((acc, productSummary)=>{
                if (productSummary.shopifyProductVariantId !== undefined && productSummary.amount > 0) {
                    acc.push({
                        id: productSummary.shopifyProductVariantId,
                        quantity: productSummary.amount,
                        additionalDescription: productSummary.additionalDescription,
                        includeConfigurationUrl: false
                    });
                }
                return acc;
            }, []);
            if (s.$linkProduct.shopifyProductVariantId !== undefined) {
                productsForCart.push({
                    id: s.$linkProduct.shopifyProductVariantId,
                    quantity: 1,
                    includeConfigurationUrl: true
                });
            }
            return productsForCart;
        },
        $price: (s)=>{
            return s.$products.reduce((acc, component)=>acc + component.price, 0);
        },
        $summary: (s)=>{
            const reducedComponents = s.$products.reduce((acc, component)=>{
                if (acc[component.key]) {
                    acc[component.key].amount += 1;
                    acc[component.key].total += component.price;
                } else {
                    acc[component.key] = {
                        ...component,
                        amount: 1,
                        total: component.price
                    };
                }
                return acc;
            }, {});
            return Object.values(reducedComponents).sort((a, b)=>a.title.localeCompare(b.title));
        },
        $sketchingRecipes: ()=>{
            return [
                {
                    title: 'Flat Surface',
                    thumbnailUrl: '/images/icons/rectangle.svg',
                    recipes: [
                        createRecipe({
                            type: 'DeckSection',
                            x: 0,
                            y: 0,
                            length: 0,
                            angle: 0,
                            balustrade: true
                        })
                    ]
                },
                {
                    title: 'Stairs',
                    thumbnailUrl: '/images/icons/stairs.svg',
                    recipes: [
                        createRecipe({
                            type: 'Stairs',
                            x: 0,
                            y: 0,
                            width: 1000,
                            angle: 0,
                            balustradeLeft: true,
                            balustradeRight: true,
                            numberOfSteps: 11,
                            goingDistance: 250,
                            riserDistance: 185
                        })
                    ]
                }
            ];
        }
    }
});

//# sourceMappingURL=deck.js.map